import React, { useEffect, useRef } from "react";
import Modal from "../base/Modal";

const DataSourceNodeModal = ({
  mode,
  nodeData,
  onClose,
  onSave,
  onDelete,
  dataSources,
  viewOnly,
}) => {
  // Manage a single selected source
  const [selectedSource, setSelectedSource] = React.useState(
    nodeData?.data?.selectedSource || null
  );
  
  // Ref for focus management
  const selectRef = useRef(null);

  useEffect(() => {
    if (!viewOnly && selectRef.current) {
      selectRef.current.focus();
    }
  }, [viewOnly]);

  const handleSelectSource = (sourceId) => {
    const source = dataSources.find((s) => s._id === sourceId);
    if (source) {
      setSelectedSource({file_id:source.file_id, timestamp: source.timestamp, original_filename: source.original_filename});
    }
  };

  const handleSave = () => {
    if (!selectedSource) {
      alert("Please select a data source.");
      return;
    }
    const updatedNode = {
      ...nodeData,
      data: {
        selectedSource,
      },
    };
    onSave(updatedNode);
  };

  // Helper: Format timestamp for details
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  return (
    <Modal
      title={viewOnly ? "Data Source" : mode === "add" ? "Add Data Source" : "Edit Data Source"}
      isOpen={true}
      onClose={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="p-4 h-[60vh] overflow-y-auto bg-zinc-900 rounded-lg shadow-md">
        <div className="p-4">
          <label
            className="block text-sm font-medium text-zinc-200 mb-2"
            htmlFor="data-source-select"
          >
            {viewOnly ? "Data Source" : "Select a Data Source"}
          </label>
          {selectedSource ? (
            <div className="flex items-center justify-between mb-4 border border-zinc-700 bg-zinc-800 p-2 rounded">
              <span className="text-zinc-200">
                {selectedSource.original_filename}
              </span>
              {!viewOnly && (
                <button
                  className="px-2 py-1 bg-zinc-800 border border-zinc-700 text-white rounded transition-colors hover:bg-zinc-700"
                  onClick={() => setSelectedSource(null)}
                  aria-label="Change selected data source"
                >
                  Change
                </button>
              )}
            </div>
          ) : (
            !viewOnly && (
              <select
                id="data-source-select"
                ref={selectRef}
                className="w-full border border-zinc-700 bg-zinc-800 text-zinc-100 p-2 rounded focus:outline-none focus:ring-2 focus:ring-[#5652CF] transition-colors mb-4"
                onChange={(e) => handleSelectSource(e.target.value)}
                value=""
                aria-label="Select a data source"
              >
                <option value="" disabled>
                  Select a data source
                </option>
                {dataSources.map((source) => (
                  <option key={source._id} value={source._id}>
                    {source.original_filename}
                  </option>
                ))}
              </select>
            )
          )}

          {selectedSource && (
            <div className="mb-4">
              <div className="w-full border border-zinc-700 bg-zinc-800 text-zinc-100 p-2 rounded flex flex-col transition-all duration-200">
                <span className="text-zinc-200">
                  {selectedSource.original_filename}
                </span>
                <div className="mt-1 text-xs text-zinc-400">
                  <p>
                    <strong>Timestamp:</strong>{" "}
                    {selectedSource.timestamp ? formatTimestamp(selectedSource.timestamp) : "N/A"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {!viewOnly && (
            <div className="flex justify-end gap-3 mt-4">
              {mode === "edit" && (
                <button
                  className="px-4 py-2 text-zinc-300 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-zinc-500"
                  onClick={() => onDelete(nodeData.id)}
                  aria-label="Delete data source node"
                >
                  Delete
                </button>
              )}
              <button
                className="px-4 py-2 text-zinc-300 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-zinc-500"
                onClick={handleSave}
                aria-label="Save data source node"
              >
                {mode === "add" ? "Add" : "Save"}
              </button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default DataSourceNodeModal;
